/**
 * Form component for canvas rendering.
 * Displays a form with various field types and handles submission.
 *
 * Task: T050 (Form component), T060 (preserve values on failure)
 */

import React, { memo, useState, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import type { FormProps as FormPropsType, FormField, FormFieldType } from '../../../types/canvas';

export interface FormComponentProps extends FormPropsType {
  onAction?: (action: { name: string; params?: Record<string, unknown> }) => Promise<void>;
  loading?: boolean;
  error?: string;
}

interface FieldProps {
  field: FormField;
  value: unknown;
  onChange: (name: string, value: unknown) => void;
  disabled?: boolean;
}

function FormFieldRenderer({ field, value, onChange, disabled }: FieldProps) {
  const baseInputClasses =
    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:cursor-not-allowed';
  const labelClasses = 'block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const target = e.target;
    if (target instanceof HTMLInputElement && target.type === 'checkbox') {
      onChange(field.name, target.checked);
    } else {
      onChange(field.name, target.value);
    }
  };

  switch (field.type) {
    case 'textarea':
      return (
        <div className="mb-4">
          <label className={labelClasses}>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <textarea
            name={field.name}
            value={String(value ?? '')}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
            disabled={disabled}
            rows={4}
            className={baseInputClasses}
          />
        </div>
      );

    case 'select':
      return (
        <div className="mb-4">
          <label className={labelClasses}>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <select
            name={field.name}
            value={String(value ?? '')}
            onChange={handleChange}
            required={field.required}
            disabled={disabled}
            className={baseInputClasses}
          >
            <option value="">{field.placeholder || 'Select an option'}</option>
            {field.options?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      );

    case 'checkbox':
      return (
        <div className="mb-4 flex items-center gap-2">
          <input
            type="checkbox"
            name={field.name}
            checked={Boolean(value)}
            onChange={handleChange}
            disabled={disabled}
            className="w-4 h-4 text-blue-600 border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 rounded focus:ring-blue-500"
          />
          <label className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        </div>
      );

    default: {
      // text, email, password, number
      const inputType: Record<FormFieldType, string> = {
        text: 'text',
        email: 'email',
        password: 'password',
        number: 'number',
        textarea: 'text',
        select: 'text',
        checkbox: 'checkbox',
      };

      return (
        <div className="mb-4">
          <label className={labelClasses}>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </label>
          <input
            type={inputType[field.type] || 'text'}
            name={field.name}
            value={String(value ?? '')}
            onChange={handleChange}
            placeholder={field.placeholder}
            required={field.required}
            disabled={disabled}
            className={baseInputClasses}
          />
        </div>
      );
    }
  }
}

function FormComponentBase({
  fields,
  submitLabel = 'Submit',
  onSubmit,
  onAction,
  loading = false,
  error,
}: FormComponentProps) {
  // T060: Initialize form values with defaultValues and preserve on failure
  const [formValues, setFormValues] = useState<Record<string, unknown>>(() =>
    fields.reduce<Record<string, unknown>>((acc, field) => {
      acc[field.name] = field.defaultValue ?? (field.type === 'checkbox' ? false : '');
      return acc;
    }, {})
  );

  const handleFieldChange = useCallback((name: string, value: unknown) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      if (!onAction || !onSubmit?.action) return;

      await onAction({
        name: onSubmit.action,
        params: formValues,
      });
    },
    [onAction, onSubmit, formValues]
  );

  return (
    <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
      {fields.map((field) => (
        <FormFieldRenderer
          key={field.name}
          field={field}
          value={formValues[field.name]}
          onChange={handleFieldChange}
          disabled={loading}
        />
      ))}

      {/* T060: Error display with preserved values */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-700 dark:text-red-400">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={loading || !onSubmit?.action}
        className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:bg-gray-400 dark:disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
      >
        {loading && <Loader2 size={16} className="animate-spin" />}
        {submitLabel}
      </button>
    </form>
  );
}

export const FormComponent = memo(FormComponentBase);
